import { Badge, Button, formatDuration, formatPrice } from "@coucou/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SALON_ID, api } from "../lib/trpc";

export type TeamMember = Awaited<ReturnType<typeof api.admin.team.list.query>>[number];

export const CONTRACT_TYPES = [
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "apprentissage", label: "Apprentissage" },
  { value: "stage", label: "Stage" },
  { value: "freelance", label: "Indépendante" },
] as const;

/** Palette d'agenda : des teintes assez distinctes pour se lire d'un coup d'œil. */
const CALENDAR_COLORS = [
  "#d96682",
  "#6f9478",
  "#d9963f",
  "#7b6ea8",
  "#4d8f9c",
  "#c25151",
  "#8a7c81",
];

/* -------------------------------------------------------------------------- */
/*  Création                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Ajout d'une employée.
 *
 * Le mot de passe est **provisoire par construction** : `mustChangePassword`
 * est posé côté serveur, l'employée devra le changer au premier accès. Une
 * gérante ne doit jamais connaître durablement le mot de passe de son équipe.
 */
export function CreateStaffDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    displayName: "",
    role: "artist" as "artist" | "manager",
    calendarColor: CALENDAR_COLORS[0]!,
    specialties: "",
    temporaryPassword: suggestPassword(),
    showInPrintedPlanning: true,
    sousContrat: true,
    contractType: "cdi" as (typeof CONTRACT_TYPES)[number]["value"],
    contractStartedAt: new Date().toISOString().slice(0, 10),
    contractEndsAt: "",
    annualLeaveDays: 25,
    weeklyHours: 35,
  });

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const create = useMutation({
    mutationFn: () =>
      api.admin.team.create.mutate({
        salonId: SALON_ID,
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim() || undefined,
        email: draft.email.trim(),
        phone: draft.phone.trim() || undefined,
        // Le prénom d'usage sert en cabine : à défaut, le prénom civil.
        displayName: draft.displayName.trim() || draft.firstName.trim(),
        role: draft.role,
        calendarColor: draft.calendarColor,
        specialties: draft.specialties
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        temporaryPassword: draft.temporaryPassword,
        showInPrintedPlanning: draft.showInPrintedPlanning,
        /*
         * Sans contrat, on n'envoie RIEN plutot que des zeros.
         *
         * Une gerante mandataire n'a ni type de contrat, ni heures
         * hebdomadaires, ni droit a conges payes : ecrire « CDI 0 h » aurait
         * rempli sa fiche de faux, et l'ecran des soldes lui aurait affiche
         * un droit qu'elle n'a pas. Les colonnes acceptent NULL, c'est la
         * reponse juste.
         */
        contractType: draft.sousContrat ? draft.contractType : undefined,
        contractStartedAt: draft.sousContrat ? draft.contractStartedAt : undefined,
        contractEndsAt: draft.sousContrat ? draft.contractEndsAt || undefined : undefined,
        annualLeaveDays: draft.sousContrat ? draft.annualLeaveDays : 0,
        weeklyContractMinutes: draft.sousContrat
          ? Math.round(draft.weeklyHours * 60)
          : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "team"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "balances"] });
      onClose();
    },
  });

  const ready = draft.firstName.trim() && draft.email.trim() && draft.temporaryPassword.length >= 10;

  return (
    <Dialog title="Nouvelle employée" onClose={onClose}>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Prénom">
          <Input value={draft.firstName} onChange={(value) => set("firstName", value)} required />
        </Field>
        <Field label="Nom">
          <Input value={draft.lastName} onChange={(value) => set("lastName", value)} />
        </Field>

        <Field label="Prénom d'usage (en cabine)" className="sm:col-span-2">
          <Input
            value={draft.displayName}
            onChange={(value) => set("displayName", value)}
            placeholder={draft.firstName || "Comme le prénom"}
          />
        </Field>

        <Field label="E-mail (identifiant de connexion)">
          <Input type="email" value={draft.email} onChange={(value) => set("email", value)} required />
        </Field>
        <Field label="Téléphone">
          <Input value={draft.phone} onChange={(value) => set("phone", value)} inputMode="tel" />
        </Field>

        <Field label="Rôle">
          <select
            value={draft.role}
            onChange={(event) => set("role", event.target.value as typeof draft.role)}
            className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
          >
            <option value="artist">Employée</option>
            <option value="manager">Gérante (accès aux réglages)</option>
          </select>
        </Field>

        <Field label="Couleur dans le planning">
          <ColorPicker
            value={draft.calendarColor}
            onChange={(value) => set("calendarColor", value)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-encre-700 sm:col-span-2">
          <input
            type="checkbox"
            checked={draft.showInPrintedPlanning}
            onChange={(event) => set("showInPrintedPlanning", event.target.checked)}
          />
          Figure sur le planning imprimé
        </label>

        <Field label="Spécialités (séparées par des virgules)" className="sm:col-span-2">
          <Input
            value={draft.specialties}
            onChange={(value) => set("specialties", value)}
            placeholder="nail art, chrome, extensions gel"
          />
        </Field>

        <ContractFields draft={draft} set={set} />

        <Field label="Mot de passe provisoire" className="sm:col-span-2">
          <div className="flex gap-2">
            <Input
              value={draft.temporaryPassword}
              onChange={(value) => set("temporaryPassword", value)}
              required
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => set("temporaryPassword", suggestPassword())}
            >
              Regénérer
            </Button>
          </div>
          <span className="mt-1 block text-xs text-encre-400">
            À transmettre de vive voix. Elle devra le changer à sa première connexion.
          </span>
        </Field>

        {create.isError && (
          <p role="alert" className="text-sm font-medium text-grenat-700 sm:col-span-2">
            {create.error instanceof Error ? create.error.message : "Création impossible."}
          </p>
        )}

        <div className="sm:col-span-2">
          <Button type="submit" loading={create.isPending} disabled={!ready}>
            Créer l&apos;employée
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Édition                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fiche employée complète : identité, contrat, compétences.
 *
 * Les compétences ne sont pas un détail : elles décident de ce que le moteur
 * de disponibilité propose. Une employée sans compétence cochée n'apparaît
 * jamais dans le tunnel de réservation, quels que soient ses horaires.
 */
export function StaffEditor({ member, onClose }: { member: TeamMember; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"profile" | "skills" | "merge">("profile");

  const [draft, setDraft] = useState({
    firstName: member.firstName,
    lastName: member.lastName ?? "",
    email: member.email ?? "",
    phone: member.phone ?? "",
    displayName: member.displayName,
    bio: member.bio ?? "",
    instagramHandle: member.instagramHandle ?? "",
    specialties: (member.specialties ?? []).join(", "),
    calendarColor: member.calendarColor,
    role: (member.role === "manager" ? "manager" : "artist") as "artist" | "manager",
    isBookableOnline: member.isBookableOnline,
    acceptsNewClients: member.acceptsNewClients,
    sortOrder: member.sortOrder,
    showInPrintedPlanning: member.showInPrintedPlanning,
    // Un type de contrat absent EST l'information : cette personne n'est pas
    // salariee. On ne le devine pas depuis le role, qui peut changer seul.
    sousContrat: member.contractType !== null,
    contractType: (member.contractType ?? "cdi") as (typeof CONTRACT_TYPES)[number]["value"],
    contractStartedAt: member.contractStartedAt ?? "",
    contractEndsAt: member.contractEndsAt ?? "",
    annualLeaveDays: member.annualLeaveDays ?? 25,
    weeklyHours: (member.weeklyContractMinutes ?? 2100) / 60,
  });

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: () =>
      api.admin.team.update.mutate({
        staffId: member.id,
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim() || null,
        // `undefined` et non `null` quand le champ est vide : l'e-mail est
        // l'identifiant de connexion, l'effacer par inadvertance couperait
        // l'accès au compte. On ne l'écrit que s'il change.
        email: draft.email.trim() && draft.email.trim() !== member.email
          ? draft.email.trim()
          : undefined,
        phone: draft.phone.trim() || null,
        displayName: draft.displayName.trim(),
        bio: draft.bio.trim() || null,
        instagramHandle: draft.instagramHandle.trim() || null,
        specialties: draft.specialties
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        calendarColor: draft.calendarColor,
        role: draft.role,
        isBookableOnline: draft.isBookableOnline,
        acceptsNewClients: draft.acceptsNewClients,
        sortOrder: draft.sortOrder,
        showInPrintedPlanning: draft.showInPrintedPlanning,
        // `null` et non `undefined` : decocher « salariee » doit EFFACER le
        // contrat, pas laisser en base celui qu'on vient de retirer.
        contractType: draft.sousContrat ? draft.contractType : null,
        contractStartedAt: draft.sousContrat ? draft.contractStartedAt || null : null,
        contractEndsAt: draft.sousContrat ? draft.contractEndsAt || null : null,
        annualLeaveDays: draft.sousContrat ? draft.annualLeaveDays : 0,
        weeklyContractMinutes: draft.sousContrat ? Math.round(draft.weeklyHours * 60) : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "team"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "balances"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
    },
  });

  const resetPassword = useMutation({
    mutationFn: (temporaryPassword: string) =>
      api.admin.team.resetPassword.mutate({ staffId: member.id, temporaryPassword }),
  });

  return (
    <Dialog title={member.displayName} onClose={onClose}>
      <div className="mb-4 flex gap-1">
        {(
          [
            ["profile", "Profil et contrat"],
            ["skills", "Prestations réalisées"],
            ["merge", "Fiche cliente"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            aria-pressed={tab === key}
            className={[
              "rounded-full px-3 py-1.5 text-sm transition-colors",
              tab === key ? "bg-blush-500 text-white" : "bg-creme-100 text-encre-700",
            ].join(" ")}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "merge" ? (
        <FusionFicheCliente member={member} />
      ) : tab === "profile" ? (
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field label="Prénom">
            <Input value={draft.firstName} onChange={(value) => set("firstName", value)} />
          </Field>
          <Field label="Nom">
            <Input value={draft.lastName} onChange={(value) => set("lastName", value)} />
          </Field>

          <Field label="Prénom d'usage">
            <Input value={draft.displayName} onChange={(value) => set("displayName", value)} />
          </Field>
          <Field label="E-mail (identifiant de connexion)">
            <Input type="email" value={draft.email} onChange={(value) => set("email", value)} />
          </Field>
          <Field label="Téléphone">
            <Input value={draft.phone} onChange={(value) => set("phone", value)} inputMode="tel" />
          </Field>

          <Field label="Présentation (affichée sur le site)" className="sm:col-span-2">
            <textarea
              value={draft.bio}
              onChange={(event) => set("bio", event.target.value)}
              rows={3}
              className="w-full rounded-lg border border-creme-300 p-3 text-sm leading-relaxed"
            />
          </Field>

          <Field label="Spécialités (virgules)">
            <Input value={draft.specialties} onChange={(value) => set("specialties", value)} />
          </Field>
          <Field label="Instagram">
            <Input
              value={draft.instagramHandle}
              onChange={(value) => set("instagramHandle", value)}
              placeholder="sans le @"
            />
          </Field>

          <Field label="Rôle">
            <select
              value={draft.role}
              onChange={(event) => set("role", event.target.value as typeof draft.role)}
              className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
            >
              <option value="artist">Employée</option>
              <option value="manager">Gérante</option>
            </select>
          </Field>
          <Field label="Couleur dans le planning">
            <ColorPicker value={draft.calendarColor} onChange={(value) => set("calendarColor", value)} />
          </Field>

          <ContractFields draft={draft} set={set} />

          <label className="flex items-center gap-2 text-sm text-encre-700">
            <input
              type="checkbox"
              checked={draft.isBookableOnline}
              onChange={(event) => set("isBookableOnline", event.target.checked)}
            />
            Réservable en ligne
          </label>
          <label className="flex items-center gap-2 text-sm text-encre-700">
            <input
              type="checkbox"
              checked={draft.acceptsNewClients}
              onChange={(event) => set("acceptsNewClients", event.target.checked)}
            />
            Accepte de nouvelles clientes
          </label>
          {/*
            Distinct de « Réservable en ligne » : c'est l'affiche de la salle
            de pause, pas le tunnel de réservation.
          */}
          <label className="flex items-center gap-2 text-sm text-encre-700">
            <input
              type="checkbox"
              checked={draft.showInPrintedPlanning}
              onChange={(event) => set("showInPrintedPlanning", event.target.checked)}
            />
            Figure sur le planning imprimé
          </label>

          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button type="submit" loading={save.isPending}>
              Enregistrer
            </Button>
            {save.isSuccess && (
              <span role="status" className="text-sm text-sauge-700">
                Enregistré.
              </span>
            )}

            {/* Réinitialiser déconnecte partout : c'est le geste qu'on fait
                quand on pense qu'un accès a fuité. */}
            <Button
              type="button"
              variant="ghost"
              className="ml-auto"
              loading={resetPassword.isPending}
              onClick={() => {
                const next = suggestPassword();
                if (
                  window.confirm(
                    `Nouveau mot de passe provisoire :\n\n${next}\n\nNotez-le : il ne sera plus affiché. Toutes ses sessions seront fermées.`,
                  )
                ) {
                  resetPassword.mutate(next);
                }
              }}
            >
              Réinitialiser le mot de passe
            </Button>
          </div>

          {resetPassword.isSuccess && (
            <p role="status" className="text-sm text-sauge-700 sm:col-span-2">
              Mot de passe réinitialisé, sessions fermées.
            </p>
          )}
        </form>
      ) : (
        <SkillsEditor staffId={member.id} />
      )}
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Compétences                                                                */
/* -------------------------------------------------------------------------- */

function SkillsEditor({ staffId }: { staffId: string }) {
  const queryClient = useQueryClient();

  const services = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api.admin.catalog.services.query({ salonId: SALON_ID }),
  });

  const skills = useQuery({
    queryKey: ["admin", "skills", staffId],
    queryFn: () => api.admin.team.skills.query({ staffId }),
  });

  const setSkill = useMutation({
    mutationFn: (input: Parameters<typeof api.admin.team.setSkill.mutate>[0]) =>
      api.admin.team.setSkill.mutate(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "skills", staffId] }),
  });

  const skillOf = (serviceId: string) =>
    skills.data?.find((entry) => entry.serviceId === serviceId);

  return (
    <div>
      <p className="mb-3 text-sm text-encre-500">
        Une prestation non cochée n&apos;est jamais proposée avec cette employée, quels que
        soient ses horaires. La durée peut être ajustée : une junior pose plus lentement.
      </p>

      <ul className="space-y-1.5">
        {services.data
          ?.filter((service) => !service.deletedAt)
          .map((service) => {
            const skill = skillOf(service.id);
            const active = skill?.isActive ?? false;

            return (
              <li
                key={service.id}
                className="flex flex-wrap items-center gap-3 rounded-lg bg-creme-50 p-3"
              >
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(event) =>
                      setSkill.mutate({
                        staffId,
                        serviceId: service.id,
                        isActive: event.target.checked,
                      })
                    }
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-encre-800">{service.name}</span>
                    <span className="block text-xs text-encre-400">
                      {formatDuration(service.durationMinutes)} ·{" "}
                      {formatPrice(service.priceCents)}
                    </span>
                  </span>
                </label>

                {active && (
                  <label className="flex items-center gap-1.5 text-sm text-encre-600">
                    Durée
                    <input
                      type="number"
                      min={5}
                      max={600}
                      step={5}
                      defaultValue={skill?.durationOverrideMinutes ?? service.durationMinutes}
                      onBlur={(event) => {
                        const value = Number(event.target.value);
                        setSkill.mutate({
                          staffId,
                          serviceId: service.id,
                          isActive: true,
                          // Même durée que le catalogue ⇒ pas de surcharge :
                          // elle suivra automatiquement une évolution du tarif.
                          durationOverrideMinutes:
                            value === service.durationMinutes ? null : value,
                        });
                      }}
                      className="h-9 w-20 rounded-lg border border-creme-300 px-2 tabular-nums"
                    />
                    min
                  </label>
                )}

                {skill?.durationOverrideMinutes && (
                  <Badge tone="warning">personnalisée</Badge>
                )}
              </li>
            );
          })}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Éléments partagés                                                          */
/* -------------------------------------------------------------------------- */

type ContractDraft = {
  sousContrat: boolean;
  contractType: (typeof CONTRACT_TYPES)[number]["value"];
  contractStartedAt: string;
  contractEndsAt: string;
  annualLeaveDays: number;
  weeklyHours: number;
};

function ContractFields<T extends ContractDraft>({
  draft,
  set,
}: {
  draft: T;
  set: <K extends keyof T>(key: K, value: T[K]) => void;
}) {
  return (
    <>
      {/*
        Tout le monde n'est pas salarie.

        Une gerante mandataire, une prothesiste independante : ni contrat, ni
        heures hebdomadaires, ni droit a conges payes. Le formulaire imposait
        pourtant « CDI 35 h » a toute nouvelle fiche, qu'on n'avait plus qu'a
        subir. La case decide, et les champs disparaissent avec elle.
      */}
      <label className="flex items-center gap-2 text-sm text-encre-700 sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.sousContrat}
          onChange={(event) => set("sousContrat" as keyof T, event.target.checked as T[keyof T])}
        />
        Salariée (contrat de travail)
      </label>

      {!draft.sousContrat && (
        <p className="text-xs text-encre-400 sm:col-span-2">
          Sans contrat : ni heures hebdomadaires, ni droit à congés payés. Les horaires de
          présence se saisissent malgré tout dans « Horaires ».
        </p>
      )}

      {draft.sousContrat && (
        <>
          <Field label="Type de contrat">
            <select
              value={draft.contractType}
              onChange={(event) => set("contractType" as keyof T, event.target.value as T[keyof T])}
              className="h-10 w-full rounded-lg border border-creme-300 bg-white px-3"
            >
              {CONTRACT_TYPES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Heures par semaine">
            <input
              type="number"
              min={0}
              max={60}
              step={0.5}
              value={draft.weeklyHours}
              onChange={(event) =>
                set("weeklyHours" as keyof T, Number(event.target.value) as T[keyof T])
              }
              className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
            />
          </Field>

          <Field label="Début du contrat">
            <input
              type="date"
              value={draft.contractStartedAt}
              onChange={(event) =>
                set("contractStartedAt" as keyof T, event.target.value as T[keyof T])
              }
              className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums"
            />
          </Field>

          <Field label={draft.contractType === "cdi" ? "Fin (sans objet en CDI)" : "Fin du contrat"}>
            <input
              type="date"
              value={draft.contractEndsAt}
              disabled={draft.contractType === "cdi"}
              onChange={(event) => set("contractEndsAt" as keyof T, event.target.value as T[keyof T])}
              className="h-10 w-full rounded-lg border border-creme-300 px-3 tabular-nums disabled:bg-creme-100"
            />
          </Field>

          <Field label="Droit annuel de congés (jours)" className="sm:col-span-2">
            <input
              type="number"
              min={0}
              max={60}
              step={0.5}
              value={draft.annualLeaveDays}
              onChange={(event) =>
                set("annualLeaveDays" as keyof T, Number(event.target.value) as T[keyof T])
              }
              className="h-10 w-32 rounded-lg border border-creme-300 px-3 tabular-nums"
            />
            <span className="mt-1 block text-xs text-encre-400">
              25 jours pour un temps plein. Les ajustements d&apos;une année précise (ancienneté,
              report) se règlent dans l&apos;onglet Congés.
            </span>
          </Field>
        </>
      )}
    </>
  );
}

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-encre-900/30 p-6 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-lift animate-fade-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-2xl">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full px-2 text-encre-400 hover:bg-creme-100"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={className}>
      <span className="mb-1 block text-sm font-medium text-encre-700">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  return (
    <input
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-lg border border-creme-300 px-3"
    />
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex h-10 items-center gap-1.5">
      {CALENDAR_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          aria-label={`Couleur ${color}`}
          aria-pressed={value === color}
          className={[
            "size-7 rounded-full transition-transform",
            value === color ? "ring-2 ring-encre-800 ring-offset-2" : "hover:scale-110",
          ].join(" ")}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

/**
 * Mot de passe provisoire lisible à voix haute.
 * Sans ambiguïté visuelle : ni O/0, ni l/1 — il sera dicté au comptoir.
 */
function suggestPassword(): string {
  const alphabet = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
}

/**
 * Rattacher une fiche cliente a une employee.
 *
 * Le cas : l'employee est aussi cliente du salon, et se retrouve avec deux
 * fiches — le numero de telephone etant unique, il ne peut vivre que sur
 * l'une des deux. L'operation repointe l'historique de cliente sur la fiche
 * d'employee, puis desactive le doublon.
 *
 * Une confirmation explicite, avec le detail de ce qui va bouger, parce que
 * l'operation n'est pas defaisable : une fois les rendez-vous repointes, rien
 * ne dit lesquels venaient de quelle fiche.
 */
function FusionFicheCliente({ member }: { member: TeamMember }) {
  const queryClient = useQueryClient();
  const [terme, setTerme] = useState("");
  const [choisie, setChoisie] = useState<{ id: string; label: string } | null>(null);

  const recherche = useQuery({
    queryKey: ["fusion-clientes", terme],
    queryFn: () => api.agenda.searchClients.query({ salonId: SALON_ID, query: terme }),
    enabled: terme.trim().length >= 2,
  });

  const fusionner = useMutation({
    mutationFn: (clientId: string) =>
      api.admin.team.mergeClientRecord.mutate({ staffId: member.id, clientId }),
    onSuccess: () => {
      setChoisie(null);
      setTerme("");
      queryClient.invalidateQueries();
    },
  });

  if (fusionner.isSuccess) {
    const r = fusionner.data;
    return (
      <div className="rounded-xl border border-sauge-500/30 bg-sauge-100 p-5">
        <p className="font-display text-lg text-sauge-700">Fiches rassemblées</p>
        <ul className="mt-3 space-y-1 text-sm text-encre-700">
          <li>{r.rendezVous} rendez-vous rattaché(s)</li>
          <li>{r.paiements} encaissement(s) rattaché(s)</li>
          <li>{r.conversations} conversation(s) rattachée(s)</li>
          <li>{r.identites} identité(s) de contact rattachée(s)</li>
          {/* Le cas le plus courant est le simple rattachement : sans cette
              ligne, le resume n'annoncait que des zeros alors que tout
              l'historique de visites venait de changer de fiche. */}
          {r.ficheClienteDeplacee && <li>Son historique de cliente est désormais rattaché.</li>}
          {r.ficheClienteFusionnee && <li>Les deux historiques de visites ont été additionnés.</li>}
          {r.telephoneRepris && <li>Le numéro de téléphone a été repris.</li>}
          {r.emailRepris && <li>L&apos;adresse e-mail a été reprise.</li>}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-encre-600">
        Si {member.displayName} est aussi cliente du salon, elle a deux fiches. Rattachez-les
        pour retrouver son historique de cliente depuis sa fiche d&apos;employée.
      </p>

      <label className="block">
        <span className="mb-1 block text-sm text-encre-600">
          Chercher la fiche cliente (nom ou téléphone)
        </span>
        <input
          value={terme}
          onChange={(event) => {
            setTerme(event.target.value);
            setChoisie(null);
          }}
          placeholder="Nom, prénom ou numéro"
          className="h-10 w-full rounded-lg border border-creme-300 px-3 text-sm"
        />
      </label>

      {recherche.data && recherche.data.length > 0 && !choisie && (
        <ul className="divide-y divide-creme-200 overflow-hidden rounded-lg border border-creme-200">
          {recherche.data
            // On ne se propose pas soi-meme : la fiche est deja la bonne.
            .filter((client) => client.id !== member.id)
            .map((client) => (
              <li key={client.id}>
                <button
                  type="button"
                  onClick={() =>
                    setChoisie({
                      id: client.id,
                      label: [client.firstName, client.lastName].filter(Boolean).join(" "),
                    })
                  }
                  className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-creme-100"
                >
                  <span>{[client.firstName, client.lastName].filter(Boolean).join(" ")}</span>
                  <span className="text-encre-400">{client.phone}</span>
                </button>
              </li>
            ))}
        </ul>
      )}

      {recherche.data?.length === 0 && terme.trim().length >= 2 && (
        <p className="text-sm text-encre-500">Aucune fiche cliente ne correspond.</p>
      )}

      {choisie && (
        <div className="rounded-xl border border-ambre-500/40 bg-ambre-100 p-4">
          <p className="text-sm leading-relaxed text-encre-800">
            Rattacher la fiche de <strong>{choisie.label}</strong> à {member.displayName} ?
            Ses rendez-vous, encaissements, conversations et son historique de visites
            passeront sur la fiche de {member.displayName}. La fiche cliente sera désactivée.
          </p>
          <p className="mt-2 text-sm font-medium text-ambre-700">
            Cette opération ne peut pas être annulée.
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              size="sm"
              loading={fusionner.isPending}
              onClick={() => fusionner.mutate(choisie.id)}
            >
              Rattacher
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setChoisie(null)}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {fusionner.isError && (
        <p role="alert" className="text-sm font-medium text-grenat-700">
          {fusionner.error instanceof Error ? fusionner.error.message : "Fusion impossible."}
        </p>
      )}
    </div>
  );
}
